/**
 * @file lib/date-picker-clear-on-empty.ts
 * @description 全站 antd DatePicker「鍵盤刪除後失焦日期又恢復」修正（2026-09-25 客戶回報序45）。
 *
 * 根因（@rc-component/picker 行為）：
 *   使用者在日期輸入框用 Backspace/Delete 把文字刪光時，picker 只把「空字串」視為
 *   合法的中間輸入，並不觸發 onChange(null)；失焦時 picker 以既有值重設輸入框文字，
 *   於是日期「又恢復」。只有點擊清除「×」才會真正清空。
 *
 * 修正方式（全站一次生效，無須逐頁改寫 25+ 個 DatePicker）：
 *   在 document 以 capture 階段監聽 `focusout`：若失焦的是 antd 日期框的 input，
 *   且該 picker 的所有輸入框文字皆為空、picker 上有清除按鈕（代表目前仍有值且允許清除），
 *   則以程式觸發清除按鈕的 click——走 picker 自身的 onClear 流程，
 *   Form / 受控 / 非受控用法皆會正確收到 onChange(null)。
 *   RangePicker 僅在「起訖兩格都刪光」時清空（清除按鈕本就是整段清空）。
 */

/** 最小 DOM 介面（便於在 node 測試環境以假物件驗證） */
export interface PickerInputLike {
  value: string;
  closest(selector: string): PickerRootLike | null;
}

/** picker 根節點的最小介面 */
export interface PickerRootLike {
  querySelectorAll(selector: string): ArrayLike<{ value: string }>;
  querySelector(selector: string): { click(): void } | null;
}

/**
 * 判斷一個失焦的輸入框是否應觸發日期清空，是則回傳要點擊的清除按鈕。
 *
 * @param target 失焦事件的 target
 * @returns 應點擊的清除按鈕；不需處理時回傳 null
 */
export function findClearTargetOnBlur(target: unknown): { click(): void } | null {
  const input = target as Partial<PickerInputLike> | null;
  if (!input || typeof input.value !== 'string' || typeof input.closest !== 'function') {
    return null;
  }
  const picker = input.closest('.ant-picker');
  if (!picker) return null;

  // 所有輸入框（單日期 1 格、區間 2 格）文字都為空才清空，避免區間只刪一端就整段清掉
  const inputs = Array.from(picker.querySelectorAll('input'));
  if (inputs.length === 0 || inputs.some((el) => el.value.trim() !== '')) return null;

  // 清除按鈕只在「有值 + allowClear + 非 disabled」時渲染，故存在即代表需要清空
  return picker.querySelector('.ant-picker-clear');
}

/**
 * 安裝全站 focusout 監聽（應用啟動時呼叫一次）。
 *
 * @param doc 目標 document（預設全域 document）
 * @returns 解除監聽的函式
 */
export function installDatePickerClearOnEmpty(doc: Document = document): () => void {
  const handler = (event: FocusEvent) => {
    try {
      const clearButton = findClearTargetOnBlur(event.target);
      clearButton?.click();
    } catch (err) {
      // 純 UI 輔助：失敗不影響原有行為，僅記錄以便排查
      console.error('[date-picker-clear-on-empty] clear on blur failed', err);
    }
  };
  doc.addEventListener('focusout', handler, true);
  return () => doc.removeEventListener('focusout', handler, true);
}
